import type React from "react"
import { Mail, Trash2 } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { UserAvatar } from "../ui/avatar"

interface User {
  id: string
  name: string
  email: string
  first_name?: string
  last_name?: string
}

interface UserCardProps {
  user: User
  onSelect: (userId: string) => void
  onDelete: (userId: string) => void
}

export default function UserCard({ user, onSelect, onDelete }: UserCardProps) {
  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-2">
        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {/* Avatar and Name */}
            <div className="flex items-center gap-3 min-w-0 flex-shrink-0 w-48 lg:w-56">
              <UserAvatar 
                user={{ 
                  first_name: user.first_name, 
                  last_name: user.last_name, 
                  email: user.email 
                }} 
                size="sm" 
              />
              <h3
                className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                onClick={() => onSelect(user.id)}
              >
                {user.name}
              </h3>
            </div>

            {/* Email */}
            <div className="flex items-center gap-2 flex-shrink-0 w-64 lg:w-72">
              <Mail className="h-4 w-4 text-slate-400" />
              <a href={`mailto:${user.email}`} className="text-sm text-slate-700 hover:text-slate-900 transition-colors truncate">
                {user.email}
              </a>
            </div>
          </div>

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
            onClick={() => onDelete(user.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex justify-between items-start mb-3">
            {/* Avatar, Name and Contact Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <UserAvatar 
                  user={{ 
                    first_name: user.first_name, 
                    last_name: user.last_name, 
                    email: user.email 
                  }} 
                  size="sm" 
                />
                <h3
                  className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors"
                  onClick={() => onSelect(user.id)}
                >
                  {user.name}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" />
                <a
                  href={`mailto:${user.email}`}
                  className="text-sm text-slate-700 hover:text-slate-900 transition-colors"
                >
                  {user.email}
                </a>
              </div>
            </div>

            {/* Delete Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              onClick={() => onDelete(user.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 